import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { sistemasService } from '@/services/sistemasService'
import type { CreateSistemaRequest } from '@/models'
import { toast } from '@/components/Toast'

// Query keys
export const sistemasKeys = {
  all: ['sistemas'] as const,
  lists: () => [...sistemasKeys.all, 'list'] as const,
  list: (filters: SistemasFilters) => [...sistemasKeys.lists(), filters] as const,
  details: () => [...sistemasKeys.all, 'detail'] as const,
  detail: (id: number) => [...sistemasKeys.details(), id] as const,
}

export interface SistemasFilters {
  buscar?: string
  categoria?: string
}

// Hook para listar sistemas
export function useSistemas(filters: SistemasFilters = {}) {
  const query = useQuery({
    queryKey: sistemasKeys.list(filters),
    queryFn: () => sistemasService.getAll(filters),
    staleTime: 1000 * 60 * 5, // 5 minutos
    refetchOnMount: true, // Sempre refetch ao montar
  })

  return {
    sistemas: query.data || [],
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

// Hook para criar sistema
export function useCreateSistema() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateSistemaRequest | FormData) => sistemasService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sistemasKeys.lists() })
      queryClient.refetchQueries({ queryKey: sistemasKeys.lists(), type: 'all' })
      toast.success('Sistema criado com sucesso!')
    },
    onError: (error: Error) => {
      console.error('Erro ao criar sistema:', error)
      toast.error('Erro ao criar sistema. Tente novamente.')
    },
  })
}

// Hook para atualizar sistema
export function useUpdateSistema() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateSistemaRequest> | FormData }) =>
      sistemasService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: sistemasKeys.lists() })
      queryClient.refetchQueries({ queryKey: sistemasKeys.lists(), type: 'all' })
      queryClient.invalidateQueries({ queryKey: sistemasKeys.detail(variables.id) })
      toast.success('Sistema atualizado com sucesso!')
    },
    onError: (error: Error) => {
      console.error('Erro ao atualizar sistema:', error)
      toast.error('Erro ao atualizar sistema. Tente novamente.')
    },
  })
}

// Hook para deletar sistema
export function useDeleteSistema() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => sistemasService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sistemasKeys.lists() })
      queryClient.refetchQueries({ queryKey: sistemasKeys.lists(), type: 'all' })
      toast.success('Sistema excluído com sucesso!')
    },
    onError: (error: Error) => {
      console.error('Erro ao excluir sistema:', error)
      toast.error('Erro ao excluir sistema. Tente novamente.')
    },
  })
}

// Hook para obter detalhes de um sistema específico
export function useSistema(id: number) {
  return useQuery({
    queryKey: sistemasKeys.detail(id),
    queryFn: () => sistemasService.getById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutos
  })
}
